/* eslint-disable @typescript-eslint/no-use-before-define */
import { all, call, put, takeLatest } from 'redux-saga/effects'

import {
    emailTransferForPasswordReset,
    passwordReset,
    postBusinessRegistrationSignUp,
    postSignIn,
    postUserSignUp,
} from 'api/apiDataHelpers'
import { getRequest, getRequestV2, postRequest, postRequestV2, postRequestV3, postRequestV4, putRequest, putRequestV2 } from 'api/apiUtils'
import { finishNotificationAlert } from 'redux/alert/alert-actions'
import { fetchSystemSettings } from 'redux/settings/settings-actions'
import SettingsActionTypes from 'redux/settings/settings-types'
import { clearAccessTokens, getRefreshToken, setAccessTokens, setCookie } from 'utils/accessHelpers'
import { IS_PRODUCTION } from 'utils/helperConfig'

import {
    failureBusinessRegistrationSignUp,
    failureChangeLanguage,
    failureCheckEmailAvailability,
    failureCompleteUserSsAccessVerification,
    failureConfirmUserEmail,
    failureExternalAccountLink,
    failureFetchIpData,
    failureFetchLatestSessions,
    failureGetCompaniesRegistrationFee,
    failureGetIdentityDepositLimits,
    failureGetIdentityDevices,
    failureLogout,
    failureOnboardingFees,
    failurePasswordResetRequest,
    failurePutPersonalQuestionnaire,
    failureRefreshToken,
    failureSignOutDevice,
    failureSwitchOnboardingIntent,
    failureSwitchUserAccount,
    failureTemporaryPasswordChange,
    failureUserAddresses,
    finishGetCompaniesRegistrationFee,
    finishSignOutDevice,
    finishSwitchOnboardingIntent,
    finishSwitchUserAccount,
    finishUserAccountPermissions,
    signInAttemptExpired,
    signInAuthStart,
    signInFailure,
    signInFinish,
    signInSuccess,
    signUpFailure,
    startFetchUserAccounts as startFetchUserAccountsAction,
    startGetCompaniesRegistrationFee,
    startUserAccountPermissions,
    successAcceptAffiliateTermsAndConditions,
    successBusinessRegistrationSignUp,
    successChangeLanguage,
    successCheckEmailAvailability,
    successCheckUserEmailStatus,
    successCompleteUserSsAccessVerification,
    successConfirmUserEmail,
    successDefaultTwoFactorAuthenticationType,
    successExternalAccountLink,
    successFetchCurrentServicePlan,
    successFetchIdentity,
    successFetchIpData,
    successFetchLatestSessions,
    successFetchSsAccessToken,
    successFetchUserAccounts,
    successGetCompaniesRegistrationFee,
    successGetIdentityDepositLimits,
    successGetIdentityDevices,
    successLogout,
    successOnboardingFees,
    successPasswordResetRequest,
    successPlaygroundMode,
    successPutPersonalQuestionnaire,
    successRefreshToken,
    successResendConfirmEmail,
    successSignOutDevice,
    successSwitchOnboardingIntent,
    successSwitchUserAccount,
    successTemporaryPasswordChange,
    successUserAccountPermissions,
    successUserAddresses,
    successUserPhoneConfirmation,
    logout as userLogout,
} from './user-actions'
import UserActionTypes from './user-types'

// This is the first saga that is called on page load
function* authorizeUser({ payload: accessToken }) {
    try {
        const { data } = yield getRequest('/identity', { accessToken })

        if (data?.isCompany) {
            yield put(startUserAccountPermissions({ id: data?.userAccountId }))
            yield put(startGetCompaniesRegistrationFee())
        }
        yield put(signInSuccess(data))
        yield put(signInFinish())
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('authorizeUser')
        }
    }
}

export function* onCheckUserSession() {
    yield takeLatest(UserActionTypes.CHECK_USER_SESSION, authorizeUser)
}

function* getUserDetailsOnSignIn({ email, password, browserUniqueId, browser, recaptcha, reCaptchaVersion }) {
    const responseData = yield postSignIn({ email, password, browserUniqueId, browser, recaptcha, reCaptchaVersion })
    const {
        data: {
            accessToken,
            refreshToken,
            signInAttemptId,
            twoFactorAuthenticationType,
            maxTrustedDeviceTimeInDays,
            refreshTokenExpiresAt,
        },
    } = yield postRequestV4('/auth/signIn', responseData)

    yield authenticateUser({
        payload: {
            twoFactorAuthenticationType,
            signInAttemptId,
            maxTrustedDeviceTimeInDays,
            accessToken,
            refreshToken,
            refreshTokenExpiresAt,
        },
    })
}

function* authenticateUser({
    payload: { twoFactorAuthenticationType, signInAttemptId, maxTrustedDeviceTimeInDays, accessToken, refreshToken, refreshTokenExpiresAt },
}) {
    switch (twoFactorAuthenticationType) {
        case 'GoogleAuthenticator':
            yield put(signInAuthStart({ signInAttemptId, twoFactorAuthenticationType, maxTrustedDeviceTimeInDays }))
            break
        default: {
            setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
            // const landingLang = JSON.parse(localStorage.getItem('landingLang')) //Receive the language code, before getting identity data.
            const { data } = yield getRequest('/identity', { accessToken })

            yield put(signInSuccess(data))
            // yield !!landingLang && landingLang !== data?.languageCode && put(startChangeLanguage(landingLang))
            yield put(signInFinish())

            return data
        }
    }
}

function* changeLanguage({ payload: data }) {
    try {
        yield putRequest('/user/settings', { languageCode: data })
        yield put(successChangeLanguage(data))
    } catch (err) {
        yield put(failureChangeLanguage())
        if (!IS_PRODUCTION) {
            console.log('ChangeLanguage', data)
        }
    }
}

export function* onStartChangeLanguage() {
    yield takeLatest([UserActionTypes.START_CHANGE_LANGUAGE], changeLanguage)
}

function* signIn2FA({ payload: { verificationCode, signInAttemptId, trustedDeviceTill, browserUniqueId } }) {
    try {
        const {
            data: { accessToken, refreshToken, trustedDeviceTill: date, refreshTokenExpiresAt },
        } = yield postRequestV2('/auth/twofactor', { verificationCode, signInAttemptId, trustedDeviceTill })

        setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
        !!date && setCookie('browserUniqueId', browserUniqueId, date)
        // const landingLang = JSON.parse(localStorage.getItem('landingLang')) //Receive the language code, before getting identity data.
        const { data } = yield getRequest('/identity', { accessToken })

        yield put(signInSuccess(data))
        // yield !!landingLang && landingLang !== data?.languageCode && put(startChangeLanguage(landingLang))
        yield put(signInFinish())
    } catch (err) {
        yield put(signInFailure({ errorMessage: err?.response?.data?.errors }))
        if (err?.response?.status === 409) {
            yield put(signInAttemptExpired(true))
        }
    }
}

export function* onSignIn2FAStart() {
    yield takeLatest(UserActionTypes.SIGN_IN_2FA_START, signIn2FA)
}

function* signIn({ payload: { email, password, browserUniqueId, browser, recaptcha, reCaptchaVersion } }) {
    try {
        yield getUserDetailsOnSignIn({ email, password, browserUniqueId, browser, recaptcha, reCaptchaVersion })
    } catch (err) {
        yield put(signInFailure({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('signInFailure')
        }
    }
}

export function* onAuthenticateStart() {
    yield takeLatest(UserActionTypes.AUTHENTICATE_USER, authenticateUser)
}

export function* onSignInStart() {
    yield takeLatest(UserActionTypes.SIGN_IN_START, signIn)
}

function* getUserDetailsOnSignUp(signUpSubmitionData) {
    const {
        data: { accessToken, refreshToken, refreshTokenExpiresAt },
    } = yield postRequestV2('/auth/register', signUpSubmitionData)

    setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
    const { data } = yield getRequest('/identity', { accessToken })

    return data
}

function* signUp({ payload: signUpData }) {
    try {
        const signUpSubmitionData = yield postUserSignUp(signUpData)
        const data = yield getUserDetailsOnSignUp(signUpSubmitionData)

        yield put(signInSuccess(data))
        window.localStorage.removeItem('referralID')
        window.localStorage.removeItem('affiliateID')
        window.localStorage.removeItem('referrerKeyword')
        window.localStorage.removeItem('utm_source')
        window.localStorage.removeItem('utm_campaign')
        window.localStorage.removeItem('utm_medium')
    } catch (err) {
        yield put(signUpFailure({ errorMessage: err?.response?.data?.errors }))
    }
}

export function* onSignUpStart() {
    yield takeLatest(UserActionTypes.SIGN_UP_START, signUp)
}

function* logout() {
    try {
        yield postRequest('/auth/signOut')
        yield clearAccessTokens()
        yield put(successLogout())
        yield window.location.replace('/')
    } catch {
        yield put(failureLogout())
        yield window.location.replace('/')
        if (!IS_PRODUCTION) {
            console.log('failureLogout')
        }
    }
}

export function* onLogout() {
    yield takeLatest(UserActionTypes.LOGOUT_START, logout)
}

function* getUserAccountPermissions({ payload: { id } }) {
    try {
        const {
            data: { permissions },
        } = yield getRequest(`/user/userAccounts/${id}/permissions`)

        yield put(successUserAccountPermissions(permissions))
    } catch (error) {
        if (!IS_PRODUCTION) {
            console.log('getUserAccountPermissions', error.response.data)
        }
    }
}

export function* onGetUserAccountPermissions() {
    yield takeLatest(UserActionTypes.START_USER_ACCOUNTS_PERMISSIONS, getUserAccountPermissions)
}

function* startSwitchUserAccount({ payload: { id } }) {
    try {
        const {
            data: { accessToken, refreshToken, refreshTokenExpiresAt },
        } = yield postRequest(`/user/userAccounts/${id}/switch`)

        setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
        const { data } = yield getRequest('/identity', { accessToken })

        if (data?.isCompany) {
            yield put(startUserAccountPermissions({ id }))
            yield put(startGetCompaniesRegistrationFee())
        } else {
            yield put(finishUserAccountPermissions())
            yield put(finishGetCompaniesRegistrationFee())
        }
        yield put(finishNotificationAlert())
        yield fetchUserAddresses()
        yield put(signInSuccess(data))
        yield put(signInFinish())
    } catch (error) {
        yield put(failureSwitchUserAccount())
    } finally {
        yield put(successSwitchUserAccount())
        yield put(finishSwitchUserAccount())
    }
}

export function* onStartSwitchUserAccount() {
    yield takeLatest(UserActionTypes.START_SWITCH_USER_ACCOUNT, startSwitchUserAccount)
}

function* startSwitchOnboardingIntent() {
    try {
        const {
            data: { accessToken, refreshToken, refreshTokenExpiresAt },
        } = yield putRequestV2('/user/register-as-person')

        setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)

        const { data } = yield getRequest('/identity', { accessToken })

        yield put(finishUserAccountPermissions())
        yield put(finishGetCompaniesRegistrationFee())
        yield put(finishNotificationAlert())
        yield fetchUserAddresses()
        yield put(signInSuccess(data))
        yield put(signInFinish())
    } catch (error) {
        yield put(failureSwitchOnboardingIntent())
    } finally {
        yield put(successSwitchOnboardingIntent())
        yield put(finishSwitchOnboardingIntent())
    }
}

export function* onStartSwitchOnboardingIntent() {
    yield takeLatest(UserActionTypes.START_SWITCH_ONBOARDING_INTENT, startSwitchOnboardingIntent)
}

function* startFetchUserAccounts() {
    try {
        const { data } = yield getRequest('/user/userAccounts')

        yield put(successFetchUserAccounts(data))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('startFetchUserAccounts', err?.response?.data)
        }
    }
}

export function* onStartFetchUserAccounts() {
    yield takeLatest(UserActionTypes.START_FETCH_USER_ACCOUNTS, startFetchUserAccounts)
}

function* startFetchSsAccessToken({payload: { userId }}) {
    try {
        const {data: {accessToken}} = yield getRequest(`/sumsub/getSumsubToken/${userId}`) // TODO: Change when backend gives proper url

        yield put(successFetchSsAccessToken(accessToken))
    } catch (error) {
        yield
    }
}
export function* onStartFetchSsAccessToken() {
    yield takeLatest(UserActionTypes.START_FETCH_SS_ACCESS_TOKEN, startFetchSsAccessToken)
}

function* startUserSsAccessVerification({ payload: { userId, ssAccessToken } }) {
    try {
        // yield putRequest(`/sumsub/getSumsubToken/${userId}`, { ssAccessToken })
        const { data } = yield getRequest('/identity')

        yield put(successCompleteUserSsAccessVerification(data))
    } catch (error) {
        yield put(failureCompleteUserSsAccessVerification())
    }
}
export function* onStartUserSsAccessVerification() {
    yield takeLatest(UserActionTypes.START_COMPLETE_USER_SS_ACCESS_VERIFICATION, startUserSsAccessVerification)
}

function* startResendConfirmEmail() {
    try {
        yield postRequestV2('/user/email/verification', { DeviceType: 'Web' })
        yield put(successResendConfirmEmail())
    } catch (error) {
        yield
    }
}

export function* onStartResendConfirmEmail() {
    yield takeLatest(UserActionTypes.START_RESEND_CONFIRM_EMAIL, startResendConfirmEmail)
}

function* startPasswordResetRequest({ payload: { email } }) {
    try {
        const responseData = yield emailTransferForPasswordReset({ email })

        yield postRequestV3('/auth/reset', responseData)
        yield put(successPasswordResetRequest({ successMessage: 'Temporary password was sent to your email' }))
    } catch (err) {
        yield put(failurePasswordResetRequest({ errorMessage: err?.response?.data?.errors }))
    }
}

export function* onstartPasswordResetRequest() {
    yield takeLatest(UserActionTypes.START_PASSWORD_RESET_REQUEST, startPasswordResetRequest)
}

function* startTemporaryPasswordChange({ payload: { passwordResetRequestId, newPassword } }) {
    try {
        const responseData = yield passwordReset({ passwordResetRequestId, newPassword })

        yield postRequestV3('/auth/password', responseData)
        yield put(successTemporaryPasswordChange())
    } catch (err) {
        yield put(failureTemporaryPasswordChange({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('Error: failureTemporaryPasswordChange')
        }
    }
}

export function* onStartTemporaryPasswordChange() {
    yield takeLatest(UserActionTypes.START_TEMPORARY_PASSWORD_CHANGE, startTemporaryPasswordChange)
}

function* startRefreshToken() {
    try {
        const currentRefreshToken = yield getRefreshToken()
        const {
            data: { accessToken, refreshToken, refreshTokenExpiresAt },
        } = yield postRequest('/auth/refresh', { refreshToken: currentRefreshToken })

        yield put(successRefreshToken())
        setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
        // yield put(checkUserSession(accessToken))
    } catch (err) {
        yield put(failureRefreshToken({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('Error: failureRefreshToken')
        }
    }
}

export function* onStartRefreshToken() {
    yield takeLatest(UserActionTypes.START_REFRESH_TOKEN, startRefreshToken)
}

function* startConfirmUserEmail({ payload: { guid } }) {
    try {
        const {
            data: { userAccountNo },
        } = yield postRequest('/auth/confirmEmail', { confirmationCode: guid })

        if (getRefreshToken()) {
            const { data } = yield getRequest('/identity')

            if (userAccountNo !== data?.userAccountId) {
                yield put(userLogout())
            }
            yield put(successConfirmUserEmail(data?.isEmailConfirmed))
        } else {
            yield put(successConfirmUserEmail())
        }
    } catch (err) {
        yield put(failureConfirmUserEmail({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('Error: failureConfirmUserEmail')
        }
    }
}

export function* onStartConfirmUserEmail() {
    yield takeLatest(UserActionTypes.START_CONFIRM_USER_EMAIL, startConfirmUserEmail)
}

function* checkUserEmailStatus() {
    try {
        const { data } = yield getRequest('/identity')

        yield put(successCheckUserEmailStatus(data?.isEmailConfirmed))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('Error: checkUserEmailStatus')
        }
    }
}

export function* onCheckUserEmailStatus() {
    yield takeLatest(UserActionTypes.START_CHECK_USER_EMAIL_STATUS, checkUserEmailStatus)
}

function* startFetchLatestSessions() {
    try {
        const { data } = yield getRequestV2('/identity/security/sessions?limit=10')

        yield put(successFetchLatestSessions(data))
    } catch (err) {
        yield put(failureFetchLatestSessions())
    }
}

export function* onStartFetchLatestSessions() {
    yield takeLatest(UserActionTypes.START_FETCH_LATEST_SESSIONS, startFetchLatestSessions)
}

function* startFetchIpData() {
    try {
        const { data } = yield getRequest('/user/ip-country')

        yield put(successFetchIpData(data))
    } catch (err) {
        yield put(failureFetchIpData())
        if (!IS_PRODUCTION) {
            console.log('Error: startFetchIpData')
        }
    }
}

export function* onStartFetchIpData() {
    yield takeLatest([UserActionTypes.START_FETCH_IP_DATA], startFetchIpData)
}

function* fetchOnboardingFee({ payload: currencyCode }) {
    try {
        const { data } = yield postRequestV2('/user/fees/OnboardingFee/amounts', { amount: { currencyCode } })

        yield put(successOnboardingFees(data))
    } catch (err) {
        yield put(failureOnboardingFees({ errorMessage: [{ errorCode: 666 }] }))
        if (!IS_PRODUCTION) {
            console.log('Error: fetchOnboardingFee')
        }
    }
}

export function* onFetchOnboardingFee() {
    yield takeLatest([UserActionTypes.FETCH_ONBOARDING_FEE], fetchOnboardingFee)
}

function* fetchActiveServicePlan() {
    try {
        const { data } = yield getRequestV2('/identities/feesBaskets/assigned')

        yield put(successFetchCurrentServicePlan({ currentServicePlan: data }))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('fetchActiveServicePlan', err?.response?.data?.title)
        }
    }
}

export function* onFetchActiveServicePlan() {
    yield takeLatest([UserActionTypes.START_FETCH_ACTIVE_SERVICE_PLAN, SettingsActionTypes.FINISH_SERVICE_PLAN], fetchActiveServicePlan)
}

function* fetchUserAddresses() {
    try {
        const { data } = yield getRequest('/identity/addresses')

        yield put(successUserAddresses(data))
    } catch (err) {
        yield put(failureUserAddresses({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('fetchUserAddresses', err?.response?.data)
        }
    }
}

export function* onFetchUserAddresses() {
    yield takeLatest(UserActionTypes.FETCH_USER_ADDRESSES, fetchUserAddresses)
}

function* startPlaygroundMode() {
    try {
        yield postRequest('/identity/verify')
        yield put(successPlaygroundMode())
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('startPlayground', err?.response?.data)
        }
    }
}

export function* onStartPlaygroundMode() {
    yield takeLatest(UserActionTypes.START_PLAYGROUND_MODE, startPlaygroundMode)
}

function* startExternalAccountLinking({ payload: { code } }) {
    try {
        const { data } = yield postRequest(`/identities/externalLinks/${code}`)

        yield put(successExternalAccountLink(data))
    } catch (err) {
        if (err?.response?.data?.status === 404) {
            yield window.location.replace('/server-error-404')
        } else {
            yield put(failureExternalAccountLink({ errorMessage: err?.response?.data?.errors }))
        }
        if (!IS_PRODUCTION) {
            console.log('startExternalAccountLinking', err?.response?.data)
        }
    }
}

export function* onStartExternalAccountLinking() {
    yield takeLatest(UserActionTypes.START_EXTERNAL_ACCOUNT_LINK, startExternalAccountLinking)
}

function* fetchDefaultTwoFactorAuthenticationType() {
    try {
        const { data } = yield getRequest('/identity')

        yield put(successDefaultTwoFactorAuthenticationType(data?.defaultTwoFactorAuthenticationType))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('fetchDefaultTwoFactorAuthenticationType', err?.response?.data)
        }
    }
}

export function* onFetchDefaultTwoFactorAuthenticationType() {
    yield takeLatest(UserActionTypes.FETCH_DEFAULT_2FA_TYPE, fetchDefaultTwoFactorAuthenticationType)
}

function* startBusinessRegistrationSignUp({
    payload: {
        firstName,
        lastName,
        email,
        password,
        referral,
        languageCode,
        companyName,
        companyNumber,
        countryCode,
        companyPhone,
        isCheckBoxChecked,
        metadata,
        referer,
        affiliate,
        browserUniqueId,
        browser,
        recaptcha,
        reCaptchaVersion,
        utmCampaign,
        utmSource,
        utmMedium,
    },
}) {
    try {
        const responseData = yield postBusinessRegistrationSignUp({
            firstName,
            lastName,
            email,
            password,
            languageCode,
            companyName,
            companyNumber,
            countryCode,
            companyPhone,
            isCheckBoxChecked,
            referral,
            metadata,
            referer,
            affiliate,
            browserUniqueId,
            browser,
            recaptcha,
            reCaptchaVersion,
            utmCampaign,
            utmSource,
            utmMedium,
        })
        const {
            data: { accessToken, refreshToken, refreshTokenExpiresAt },
        } = yield postRequestV3('/auth/company/register', responseData)

        setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
        const { data } = yield getRequest('/identity', { accessToken })

        yield put(signInSuccess(data))
        yield put(successBusinessRegistrationSignUp())
    } catch (err) {
        yield put(failureBusinessRegistrationSignUp({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('Error startBusinessRegistrationSignUp', err?.response?.data)
        }
    }
}

export function* onStartBusinessRegistrationSignUp() {
    yield takeLatest(UserActionTypes.START_BUSINESS_REGISTRATION_SIGN_UP, startBusinessRegistrationSignUp)
}

function* putPersonalQuestionnaire({
    payload: {
        accountOpeningPurpose,
        accountOpeningPurposeExplanation,
        taxIdentificationNumber,
        occupation,
        occupationExplanation,
        monthlyTurnover,
        sourceOfFunds,
        sourceOfFundsExplanation,
        pepConnection,
        verificationCode,
        address
    },
}) {
    try {
        const responseData = {
            accountOpeningPurpose,
            accountOpeningPurposeExplanation,
            taxIdentificationNumber,
            occupation,
            occupationExplanation,
            monthlyTurnover,
            sourceOfFunds,
            sourceOfFundsExplanation,
            pepConnection,
            verificationCode,
            address
        }

        yield putRequest('/identity/personal-questionnaire', responseData)
        yield put(successPutPersonalQuestionnaire())
    } catch (err) {
        yield !!err?.response?.data?.errors && put(failurePutPersonalQuestionnaire({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('putPersonalQuestionnaire', err?.response?.data)
        }
    }
}

export function* onPutPersonalQuestionnaire() {
    yield takeLatest(UserActionTypes.START_PUT_PERSONAL_QUESTIONNAIRE, putPersonalQuestionnaire)
}

function* userPhoneConfirmation() {
    try {
        const { data } = yield getRequest('/identity')

        yield put(successUserPhoneConfirmation(data))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('userPhoneConfirmation', err?.response?.data)
        }
    }
}

export function* onUserPhoneConfirmation() {
    yield takeLatest(UserActionTypes.GET_USER_PHONE_CONFIRMATION, userPhoneConfirmation)
}

function* getCompaniesRegistrationFee() {
    try {
        const { data } = yield getRequest('/user/companies/registration-fee')

        yield put(successGetCompaniesRegistrationFee(data))
    } catch (err) {
        yield put(failureGetCompaniesRegistrationFee({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('getCompaniesRegistrationFee', err?.response?.data)
        }
    }
}

export function* onGetCompaniesRegistrationFee() {
    yield takeLatest(UserActionTypes.START_GET_COMPANIES_REGISTRATION_FEE, getCompaniesRegistrationFee)
}

function* fetchIdentity() {
    try {
        const { data } = yield getRequest('/identity')

        yield put(successFetchIdentity(data))
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('fetchIdentity', err?.response?.data)
        }
    }
}

export function* onFetchIdentity() {
    yield takeLatest(UserActionTypes.START_FETCH_IDENTITY, fetchIdentity)
}

function* startGetIdentityDepositLimits() {
    try {
        const { data } = yield getRequest('/identity/deposit-limits')

        yield put(successGetIdentityDepositLimits(data))
    } catch (err) {
        yield put(failureGetIdentityDepositLimits())
        if (!IS_PRODUCTION) {
            console.log('fetchIdentity', err?.response?.data)
        }
    }
}

export function* onStartGetIdentityDepositLimits() {
    yield takeLatest(UserActionTypes.START_GET_IDENTITY_DEPOSIT_LIMITS, startGetIdentityDepositLimits)
}

function* fetchData() {
    yield put(fetchSystemSettings())
    yield put(startFetchUserAccountsAction())
}

export function* onSignSuccess() {
    yield takeLatest(UserActionTypes.SIGN_IN_SUCCESS, fetchData)
}

function* acceptAffiliateTermsAndConditions() {
    try {
        yield putRequest('/identity/accept-affiliate-terms-conditions')
        yield put(successAcceptAffiliateTermsAndConditions())
    } catch (err) {
        if (!IS_PRODUCTION) {
            console.log('Accept affiliate terms and conditions', err)
        }
    }
}

export function* onAcceptAffiliateTermsAndConditions() {
    yield takeLatest(UserActionTypes.START_ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS, acceptAffiliateTermsAndConditions)
}

function* checkEmailAvailability({ payload: { email } }) {
    try {
        const { data } = yield postRequest('/user/email/check-availability', { email })

        yield put(successCheckEmailAvailability(data))
    } catch (err) {
        yield put(failureCheckEmailAvailability({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('checkEmailAvailability', err?.response?.data)
        }
    }
}
export function* onCheckEmailAvailability() {
    yield takeLatest(UserActionTypes.START_CHECK_EMAIL_AVAILABILITY, checkEmailAvailability)
}

function* startGetIdentityDevices() {
    try {
        const { data } = yield getRequest('/identity/security/logged-in')

        yield put(successGetIdentityDevices(data))
    } catch (err) {
        yield put(failureGetIdentityDevices())
        if (!IS_PRODUCTION) {
            console.log('fetchIdentityDevices', err?.response?.data)
        }
    }
}
export function* onStartGetIdentityDevices() {
    yield takeLatest(UserActionTypes.START_GET_IDENTITY_DEVICES, startGetIdentityDevices)
}

function* signOutDevice({ payload: { deviceId } }) {
    try {
        const { data } = yield postRequest('/identity/security/sign-out', { deviceId })

        yield put(successSignOutDevice(data))
        yield put(finishSignOutDevice())
    } catch (err) {
        yield put(failureSignOutDevice({ errorMessage: err?.response?.data?.errors }))
        if (!IS_PRODUCTION) {
            console.log('signOutDevice', err?.response?.data)
        }
    }
}
export function* onSignOutDevice() {
    yield takeLatest(UserActionTypes.START_SIGN_OUT_DEVICE, signOutDevice)
}

export function* userSagas() {
    yield all([
        call(onAuthenticateStart),
        call(onSignInStart),
        call(onSignIn2FAStart),
        call(onCheckUserSession),
        call(onLogout),
        call(onSignUpStart),
        call(onStartFetchSsAccessToken),
        call(onStartUserSsAccessVerification),
        call(onStartResendConfirmEmail),
        call(onstartPasswordResetRequest),
        call(onStartTemporaryPasswordChange),
        call(onSignSuccess),
        call(onFetchOnboardingFee),
        call(onStartFetchUserAccounts),
        call(onStartSwitchUserAccount),
        call(onStartSwitchOnboardingIntent),
        call(onStartRefreshToken),
        call(onFetchActiveServicePlan),
        call(onStartConfirmUserEmail),
        call(onFetchUserAddresses),
        call(onStartFetchLatestSessions),
        call(onStartFetchIpData),
        call(onGetUserAccountPermissions),
        call(onStartPlaygroundMode),
        call(onStartExternalAccountLinking),
        call(onFetchDefaultTwoFactorAuthenticationType),
        call(onStartChangeLanguage),
        call(onStartBusinessRegistrationSignUp),
        call(onPutPersonalQuestionnaire),
        call(onUserPhoneConfirmation),
        call(onGetCompaniesRegistrationFee),
        call(onCheckUserEmailStatus),
        call(onFetchIdentity),
        call(onStartGetIdentityDepositLimits),
        call(onAcceptAffiliateTermsAndConditions),
        call(onCheckEmailAvailability),
        call(onStartGetIdentityDevices),
        call(onSignOutDevice),
    ])
}
